import React, { Key, MutableRefObject, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { DataTable, DataTableColumn } from '../../../ReactRouting/Components/Molecules/DataTable/DataTable'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { IconName } from '../../../NonReact/constants/React/Icon/Constants'
import { RangeCombo } from '../../Common/Components/Selects/RangeCombo/RangeCombo'
import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import type { SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import { ReactState } from '../../Common/Types/Types'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { rangeEntryWrappersToMemberIdentifiers } from '../Utils/rangeEntryWrappersToMemberIdentifiers'

export type MemberSearchOption<TOriginal> = SelectOption<string> & {
    original?: TOriginal
}

type MemberTableRow = {
    key: Key
    Description: string
}

type Props<TRow extends MemberTableRow, TOriginal> = {
    label: string
    noRowsLabel: string
    searchAddButtonTitle: string
    removeButtonLabel: string
    searchLabel: string
    rows: TRow[]
    onRowsChange: (rows: TRow[]) => void
    columns: Array<DataTableColumn<TRow, keyof TRow>>
    getIdentifier: (row: TRow) => string
    createRowFromOption: (option: MemberSearchOption<TOriginal>) => TRow
    loadOptions: (searchQuery: string) => Promise<Array<MemberSearchOption<TOriginal>>>
    viewOnly?: boolean
    /**
     * Forwarded to {@link RangeCombo} for outlined router field styling. Member search layout is not gated on this.
     */
    isRouterMode?: boolean
    resolveValidIdentifiers?: (
        pendingRanges: Nullable<RangeEntryWrapper[]>
    ) => Promise<ReadonlyArray<MemberSearchOption<TOriginal>>>
}

/**
 * Shared device-member management table used by the Device Members, Agent Members, and DNIS tabs.
 *
 * <p>Selected members are shown in a paginated {@link DataTable}. New entries are added from a bottom search field
 * that reuses {@link RangeCombo}'s async-search behaviour, while removal happens from the selected table rows. Search
 * selections stay pending until the inline add button is clicked so multiple users can be added in one action.</p>
 *
 * @param props Component props.
 * @returns Member-management table with add/remove controls.
 */
export const MemberTable = <TRow extends MemberTableRow, TOriginal>({
    label,
    noRowsLabel,
    searchAddButtonTitle,
    removeButtonLabel,
    searchLabel,
    rows,
    onRowsChange,
    columns,
    getIdentifier,
    createRowFromOption,
    loadOptions,
    viewOnly,
    isRouterMode,
    resolveValidIdentifiers
}: Props<TRow, TOriginal>): ReactElement => {
    const memberSearchLabelIdRef: React.MutableRefObject<string> = useRef<string>(
        `member-table-search-label-${Date.now()}`
    )
    const memberSearchFieldLabelId: string = memberSearchLabelIdRef.current
    const [selectedRows, setSelectedRows]: ReactState<TRow[]> = useState<TRow[]>([])
    const [searchFieldKey, setSearchFieldKey]: ReactState<number> = useState<number>(0)
    const [pendingRanges, setPendingRanges]: ReactState<Nullable<RangeEntryWrapper[]>> =
        useState<Nullable<RangeEntryWrapper[]>>(null)
    const latestSearchOptions: MutableRefObject<Array<MemberSearchOption<TOriginal>>> = useRef<
        Array<MemberSearchOption<TOriginal>>
    >([])

    const selectedIdentifiers: string[] = useMemo(
        (): string[] => rows.map((row: TRow): string => getIdentifier(row)),
        [getIdentifier, rows]
    )

    const asyncSearchingFunction = useCallback(
        async (searchQuery: string): Promise<SelectOptions<string>> => {
            const selectedIdentifierSet: Set<string> = new Set(selectedIdentifiers)
            const options: Array<MemberSearchOption<TOriginal>> = (await loadOptions(searchQuery)).filter(
                (option: MemberSearchOption<TOriginal>): boolean => !selectedIdentifierSet.has(option.value)
            )

            latestSearchOptions.current = options

            return options
        },
        [loadOptions, selectedIdentifiers]
    )

    const handleSearchChange = useCallback((_value?: string, ranges?: Nullable<RangeEntryWrapper[]>): void => {
        setPendingRanges(ranges ?? null)
    }, [])

    const handleAddPendingClick = useCallback(async (): Promise<void> => {
        const selectedIdentifierSet: Set<string> = new Set(selectedIdentifiers)

        const resolvedOptions: OnlyNullable<ReadonlyArray<MemberSearchOption<TOriginal>>> = resolveValidIdentifiers
            ? await resolveValidIdentifiers(pendingRanges)
            : null

        const pendingIdentifiers: readonly string[] = resolvedOptions
            ? resolvedOptions.map((o: MemberSearchOption<TOriginal>): string => o.value)
            : rangeEntryWrappersToMemberIdentifiers(pendingRanges)

        const identifiersToAdd: string[] = pendingIdentifiers.filter(
            (identifier: string): boolean => !selectedIdentifierSet.has(identifier)
        )

        if (identifiersToAdd.length < 1) {
            setPendingRanges(null)
            setSearchFieldKey((currentValue: number): number => currentValue + 1)

            return
        }

        const resolvedOptionMap: ReadonlyMap<string, MemberSearchOption<TOriginal>> = resolvedOptions
            ? new Map(
                  resolvedOptions.map((o: MemberSearchOption<TOriginal>): [string, MemberSearchOption<TOriginal>] => [
                      o.value,
                      o
                  ])
              )
            : new Map<string, MemberSearchOption<TOriginal>>()

        const nextRows: TRow[] = identifiersToAdd.map((identifier: string): TRow => {
            const selectedOption: MemberSearchOption<TOriginal> = resolvedOptionMap.get(identifier) ??
                latestSearchOptions.current.find(
                    (option: MemberSearchOption<TOriginal>): boolean => option.value === identifier
                ) ?? {
                    value: identifier,
                    label: identifier
                }

            return createRowFromOption(selectedOption)
        })

        onRowsChange([...rows, ...nextRows])
        setPendingRanges(null)
        setSearchFieldKey((currentValue: number): number => currentValue + 1)
    }, [createRowFromOption, onRowsChange, pendingRanges, resolveValidIdentifiers, rows, selectedIdentifiers])

    const hasPendingSelections: boolean = !ArrayUtils.isEmpty(pendingRanges)

    const handleRemoveClick = useCallback((): void => {
        const selectedIdentifierSet: Set<string> = new Set(selectedRows.map((row: TRow): string => getIdentifier(row)))

        if (selectedIdentifierSet.size < 1) return

        onRowsChange(rows.filter((row: TRow): boolean => !selectedIdentifierSet.has(getIdentifier(row))))
        setSelectedRows([])
    }, [getIdentifier, onRowsChange, rows, selectedRows])

    useEffect((): void => {
        const validSelection: TRow[] = selectedRows.filter((row: TRow): boolean =>
            selectedIdentifiers.includes(getIdentifier(row))
        )

        if (validSelection.length !== selectedRows.length) {
            setSelectedRows(validSelection)
        }
    }, [getIdentifier, selectedIdentifiers, selectedRows])

    useHotkeys(
        'ctrl+enter',
        (event: KeyboardEvent): void => {
            event.preventDefault()
            handleAddPendingClick()
        },
        { enableOnFormTags: true, enabled: hasPendingSelections },
        [handleAddPendingClick, hasPendingSelections]
    )

    return (
        <div className='member-table' aria-label={label}>
            <DataTable
                columns={columns}
                data={rows}
                localeText={{ noRows: noRowsLabel }}
                toolbarDisabled
                isSelectable={viewOnly !== true}
                onSelectionChange={(newSelection: TRow[]): void => {
                    setSelectedRows(newSelection)
                }}
                defaultRowsPerPage={25}
                defaultRowsPerPageOptions={[25]}
            />

            {viewOnly !== true ? (
                <div className='member-table__actions'>
                    <IconButton
                        type={IconButtonType.BUTTON}
                        iconName={IconName.REMOVE}
                        title={removeButtonLabel}
                        customClassName='member-table__action-button'
                        isEnabled={!ArrayUtils.isEmpty(selectedRows)}
                        onClick={handleRemoveClick}
                    />
                </div>
            ) : null}

            {viewOnly !== true ? (
                <div className='member-table__search'>
                    {isStringNotEmpty(searchLabel) ? (
                        <div className='member-table__search-label' id={memberSearchFieldLabelId}>
                            {searchLabel}
                        </div>
                    ) : null}
                    <div className='member-table__search-controls'>
                        <RangeCombo
                            key={searchFieldKey}
                            label={isStringNotEmpty(searchLabel) ? undefined : searchLabel}
                            ariaLabelledBy={isStringNotEmpty(searchLabel) ? memberSearchFieldLabelId : undefined}
                            options={[]}
                            originalValue={EMPTY_STRING}
                            onChange={handleSearchChange}
                            asyncSearchingFunction={asyncSearchingFunction}
                            fullWidth
                            isRouterMode={isRouterMode}
                            showAsyncResultsOnEmptySearch
                            emptySearchResultCap={50}
                            disableExcluding
                        />

                        <IconButton
                            type={IconButtonType.BUTTON}
                            iconName={IconName.CHECK}
                            title={searchAddButtonTitle}
                            customClassName='member-table__search-add-button'
                            isEnabled={hasPendingSelections}
                            onClick={handleAddPendingClick}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    )
}
